import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { formatLength, degreesToPitch, stepControl, } from '../../util/units';
const css = `
.dim-panel {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: rgba(15, 28, 58, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 16px 14px;
    min-width: 230px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    user-select: none;
}
.dim-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-left: 4px;
}
.dim-panel__title {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
}
.dim-panel__unit-toggle {
    display: flex;
    background: rgba(255,255,255,0.07);
    border-radius: 6px;
    padding: 2px;
    gap: 2px;
}
.dim-panel__unit-btn {
    height: 20px;
    padding: 0 8px;
    border: none;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    background: transparent;
    color: rgba(255,255,255,0.35);
    line-height: 1;
}
.dim-panel__unit-btn--active {
    background: rgba(255,255,255,0.18);
    color: #fff;
}
.dim-panel__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 8px;
    border-radius: 10px;
    transition: background 0.15s;
    gap: 8px;
}
.dim-panel__row:hover { background: rgba(255,255,255,0.05); }
.dim-panel__label {
    font-size: 11px;
    color: rgba(255,255,255,0.6);
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.dim-panel__control {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
}
.dim-panel__btn {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    color: rgba(255,255,255,0.8);
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.12s, transform 0.1s;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
}
.dim-panel__btn:hover { background: rgba(255,255,255,0.18); }
.dim-panel__btn:active { transform: scale(0.88); background: rgba(255,255,255,0.28); }
.dim-panel__btn:disabled { opacity: 0.25; cursor: not-allowed; transform: none; }
.dim-panel__value {
    min-width: 68px;
    height: 24px;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.07);
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    padding: 0 4px;
}
.dim-panel__divider {
    height: 1px;
    background: rgba(255,255,255,0.06);
    margin: 6px 0;
}
.dim-panel__group-label {
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.2);
    padding: 2px 8px 0;
}
.dim-panel__save {
    margin-top: 10px;
    width: 100%;
    height: 38px;
    border-radius: 10px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.15s;
}
.dim-panel__save:hover { background: rgba(255,255,255,0.2); }
.dim-panel__save:active { background: rgba(255,255,255,0.08); }
`;
function groupControls(controls) {
    const main = [];
    const overhang = [];
    for (const c of controls) {
        if (c.isOverhang)
            overhang.push(c);
        else
            main.push(c);
    }
    return { main, overhang };
}
function ControlRow({ control, units }) {
    const displayValue = control.isAngle && units === 'ft'
        ? degreesToPitch(control.value)
        : formatLength(control.value, units);
    const atMin = control.min !== undefined && control.value <= control.min;
    const atMax = control.max !== undefined && control.value >= control.max;
    return (_jsxs("div", { className: "dim-panel__row", children: [_jsx("span", { className: "dim-panel__label", children: control.label }), _jsxs("div", { className: "dim-panel__control", children: [_jsx("button", { className: "dim-panel__btn", onClick: () => control.onChange(stepControl(control, -1)), disabled: atMin, children: "\u2212" }), _jsx("div", { className: "dim-panel__value", children: displayValue }), _jsx("button", { className: "dim-panel__btn", onClick: () => control.onChange(stepControl(control, 1)), disabled: atMax, children: "+" })] })] }));
}
export function DimensionPanel({ controls, onSave, title, btnText, units = 'm', onUnitsChange, }) {
    const { main, overhang } = groupControls(controls);
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: css }), _jsxs("div", { className: "dim-panel", children: [_jsxs("div", { className: "dim-panel__header", children: [_jsx("span", { className: "dim-panel__title", children: title }), onUnitsChange && (_jsxs("div", { className: "dim-panel__unit-toggle", children: [_jsx("button", { className: `dim-panel__unit-btn ${units === 'm' ? 'dim-panel__unit-btn--active' : ''}`, onClick: () => onUnitsChange('m'), children: "M" }), _jsx("button", { className: `dim-panel__unit-btn ${units === 'ft' ? 'dim-panel__unit-btn--active' : ''}`, onClick: () => onUnitsChange('ft'), children: "FT" })] }))] }), main.map((c, i) => _jsx(ControlRow, { control: c, units: units }, i)), overhang.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "dim-panel__divider" }), _jsx("div", { className: "dim-panel__group-label", children: "Overhangs" }), overhang.map((c, i) => _jsx(ControlRow, { control: c, units: units }, i))] })), onSave && (_jsx("button", { className: "dim-panel__save", onClick: onSave, children: btnText }))] })] }));
}
